/**
 * The composer: a pill that grows into a textarea, the files staged for this
 * turn, the `@`-mentions attached to it, and Send/Stop.
 *
 * It is a `<textarea>` rather than an `<input>` because the mention picker
 * needs a caret position to find the token being typed (and because a
 * two-paragraph question shouldn't scroll sideways). Enter sends, Shift+Enter
 * makes a newline. `contenteditable` is deliberately not used — the draft has
 * to stay a plain string that the mention replacement can splice.
 *
 * Dropping files onto the panel is handled by the panel itself (it owns those
 * bounds); both paths land in `attachFiles`.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  MicrophoneIcon,
  PaperClipIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { PaperAirplaneIcon, StopIcon } from '@heroicons/react/24/solid';
import AttachmentChip from '@/components/chat/AttachmentChip';
import {
  MAX_MESSAGE_CHARS,
  useAssistant,
  type PendingAttachment,
} from '@/contexts/AssistantContext';
import { searchHitLabel } from '@/services/search';
import type { AssistantMentionType } from '@/types/assistant';
import type { SearchHit } from '@/types/search';
import MentionPopover from './MentionPopover';

/** Documents the backend will actually extract text from. */
const DOCUMENT_ACCEPT = '.pdf,.docx,.txt,.md,.csv';

/** Longest `@…` fragment we keep searching on before assuming it isn't one. */
const MAX_MENTION_QUERY = 40;

const TEXTAREA_MAX_HEIGHT = 120;

interface MentionToken {
  /** Text between the `@` and the caret. */
  query: string;
  /** Index of the `@` in the draft. */
  start: number;
}

/**
 * The `@token` the caret sits in, or null. Scans backwards from the caret and
 * gives up at the first whitespace, so "email me@example.com" is not a
 * mention and "@safety bas" stops being one at the space.
 */
export function activeMentionToken(value: string, caret: number): MentionToken | null {
  for (let i = caret - 1; i >= 0; i -= 1) {
    const char = value[i];
    if (char === '@') {
      const previous = i === 0 ? ' ' : value[i - 1];
      if (!/\s/.test(previous)) return null;
      return { query: value.slice(i + 1, caret), start: i };
    }
    if (/\s/.test(char)) return null;
  }
  return null;
}

const StagedChip: React.FC<{ item: PendingAttachment; onRemove: () => void }> = ({
  item,
  onRemove,
}) => {
  const uploading = !item.error && !item.attachment;
  if (item.previewUrl) {
    return (
      <div className="relative group">
        <img
          src={item.previewUrl}
          alt={item.file.name}
          className="w-16 h-16 rounded-lg object-cover border border-border-secondary"
        />
        {uploading && (
          <span className="absolute inset-0 rounded-lg bg-black/40 flex items-center justify-center text-white text-[11px] font-medium">
            {item.progress}%
          </span>
        )}
        {item.error && (
          <span className="absolute inset-0 rounded-lg bg-error-600/70 flex items-center justify-center text-white text-[10px] px-1 text-center">
            Failed
          </span>
        )}
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${item.file.name}`}
          className="absolute -top-1.5 -right-1.5 p-0.5 rounded-full bg-neutral-900 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
        >
          <XMarkIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  return (
    <AttachmentChip
      name={item.file.name}
      mime={item.file.type}
      size={item.file.size}
      progress={uploading ? item.progress : undefined}
      error={item.error}
      onRemove={onRemove}
    />
  );
};

const AssistantComposer: React.FC = () => {
  const {
    draft,
    setDraft,
    send,
    stop,
    thinking,
    staged,
    attachFiles,
    removeStaged,
    pendingMentions,
    addMention,
    removeMention,
  } = useAssistant();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const caretToRestore = useRef<number | null>(null);
  const [mention, setMention] = useState<MentionToken | null>(null);
  const [dismissedStart, setDismissedStart] = useState<number | null>(null);
  const [activeOptionId, setActiveOptionId] = useState<string | null>(null);

  const uploading = staged.some((item) => !item.attachment && !item.error);
  const canSend = draft.trim().length > 0 && !uploading;
  const mentionOpen = !!mention && mention.start !== dismissedStart;

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
  }, []);

  useLayoutEffect(() => {
    autoResize();
  }, [draft, autoResize]);

  // The composer mounts with the panel, so mounting IS opening — the member
  // can start typing without hunting for the field.
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Put the caret back where the mention insertion left it.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el || caretToRestore.current === null) return;
    el.setSelectionRange(caretToRestore.current, caretToRestore.current);
    caretToRestore.current = null;
  }, [draft]);

  const syncMention = useCallback((el: HTMLTextAreaElement) => {
    const token = activeMentionToken(el.value, el.selectionStart ?? el.value.length);
    setMention(token && token.query.length <= MAX_MENTION_QUERY ? token : null);
  }, []);

  const handleSend = () => {
    if (!canSend) return;
    void send(draft);
    setMention(null);
    setDismissedStart(null);
  };

  const pickMention = (hit: SearchHit) => {
    const el = textareaRef.current;
    if (!el || !mention) return;
    const label = searchHitLabel(hit);
    const caret = el.selectionStart ?? el.value.length;
    const next = `${draft.slice(0, mention.start)}@${label} ${draft.slice(caret)}`;
    caretToRestore.current = mention.start + label.length + 2;
    setDraft(next);
    addMention({ type: hit.type as AssistantMentionType, id: hit.id, label });
    setMention(null);
    setDismissedStart(null);
    el.focus();
  };

  const takeFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    void attachFiles(Array.from(files));
  };

  return (
    <div className="mx-3 mb-3 shrink-0">
      {staged.length > 0 && (
        <div
          className="flex flex-wrap items-start gap-2 pb-2"
          data-testid="teri-staged-attachments"
        >
          {staged.map((item) => (
            <StagedChip
              key={item.localKey}
              item={item}
              onRemove={() => removeStaged(item.localKey)}
            />
          ))}
        </div>
      )}

      {pendingMentions.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 pb-2" data-testid="teri-mentions">
          {pendingMentions.map((mentionRef) => (
            <span
              key={`${mentionRef.type}:${mentionRef.id}`}
              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-primary-500/10 text-primary-600 dark:text-primary-350 text-[11px] font-semibold"
            >
              @{mentionRef.label}
              <button
                type="button"
                onClick={() => removeMention(mentionRef.id)}
                aria-label={`Remove ${mentionRef.label} from this message`}
                className="p-0.5 rounded-full hover:bg-primary-500/20"
              >
                <XMarkIcon className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Composer — wide pill, per design; it grows with the draft. */}
      <form
        className="min-h-12 rounded-3xl bg-white/90 dark:bg-grey-800/90 shadow-md flex items-end gap-1.5 pl-4 pr-2 py-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
      >
        <textarea
          ref={textareaRef}
          rows={1}
          value={draft}
          maxLength={MAX_MESSAGE_CHARS}
          onChange={(e) => {
            setDraft(e.target.value);
            syncMention(e.target);
          }}
          onSelect={(e) => syncMention(e.currentTarget)}
          onKeyDown={(e) => {
            // While the picker is up it owns Enter/Tab/arrows (it intercepts
            // them in the capture phase), so anything reaching here is meant
            // for the composer.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Type a message"
          aria-label="Message Teri"
          aria-expanded={mentionOpen}
          aria-activedescendant={activeOptionId ?? undefined}
          className="flex-1 min-w-0 max-h-[120px] py-1.5 resize-none bg-transparent text-sm leading-relaxed text-text-primary placeholder:text-text-tertiary outline-none scrollbar-thin"
        />

        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          data-testid="teri-image-input"
          onChange={(e) => {
            takeFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept={DOCUMENT_ACCEPT}
          multiple
          className="hidden"
          data-testid="teri-file-input"
          onChange={(e) => {
            takeFiles(e.target.files);
            e.target.value = '';
          }}
        />

        <button
          type="button"
          aria-label="Send an image"
          title="Send an image"
          onClick={() => imageInputRef.current?.click()}
          // Shown at every width: attaching is a real feature now, and the
          // phone sheet is exactly where a member photographs something.
          className="shrink-0 w-8 h-8 mb-0.5 rounded-full flex items-center justify-center text-text-tertiary hover:text-text-secondary hover:bg-text-tertiary/10 transition-colors duration-fast focus-ring"
        >
          <PhotoIcon className="w-5 h-5" />
        </button>
        <button
          type="button"
          aria-label="Attach a file"
          title="Attach a file (PDF, Word, text, CSV)"
          onClick={() => fileInputRef.current?.click()}
          className="shrink-0 w-8 h-8 mb-0.5 rounded-full flex items-center justify-center text-text-tertiary hover:text-text-secondary hover:bg-text-tertiary/10 transition-colors duration-fast focus-ring"
        >
          <PaperClipIcon className="w-5 h-5" />
        </button>
        <button
          type="button"
          aria-label="Voice message"
          title="Voice message — coming soon"
          disabled
          className="shrink-0 w-8 h-8 mb-0.5 rounded-full hidden sm:flex items-center justify-center text-text-tertiary opacity-50 cursor-not-allowed"
        >
          <MicrophoneIcon className="w-5 h-5" />
        </button>

        {/* While a reply is in flight there is only one useful action, so Send
            becomes Stop rather than sitting there disabled — filled, because
            interrupting should be the obvious move. */}
        {thinking ? (
          <button
            type="button"
            onClick={stop}
            aria-label="Stop generating"
            title="Stop generating"
            data-testid="teri-stop"
            className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 active:scale-[0.92] transition-[background-color,transform] duration-fast focus-ring"
          >
            <StopIcon className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button
            type="submit"
            aria-label="Send"
            disabled={!canSend}
            title={uploading ? 'Waiting for the upload to finish…' : 'Send'}
            className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-primary-500 hover:bg-primary-500/10 disabled:opacity-40 active:scale-[0.92] transition-[background-color,transform,opacity] duration-fast"
          >
            <PaperAirplaneIcon className="w-5 h-5" />
          </button>
        )}
      </form>

      {mentionOpen && mention && (
        <MentionPopover
          anchorRef={textareaRef}
          query={mention.query}
          onSelect={pickMention}
          onDismiss={() => setDismissedStart(mention.start)}
          onActiveOptionChange={setActiveOptionId}
        />
      )}
    </div>
  );
};

export default AssistantComposer;
